import { useEffect, useState } from "react";

/**
 * When the viewer last looked at this brand's Trends, so the wall can mark what
 * arrived since.
 *
 * Kept in this browser only, per brand: `co:trends:lastSeen:<brandId>` holds an
 * ISO instant. It is READ once when the page opens for a brand and held for the
 * whole visit, so a marker does not vanish while it is being looked at, and it
 * is WRITTEN as the viewer leaves: when the page unmounts, when the brand
 * changes under it, and when the tab is hidden. A first visit has nothing
 * stored and answers null, which the wall reads as "mark nothing".
 *
 * Storage can be blocked or full. Every read and write is guarded, and a
 * failure means the feature quietly does not run rather than the page failing.
 */

const storageKey = (brandId: string) => `co:trends:lastSeen:${brandId}`;

function readLastSeen(brandId: string | null): string | null {
  if (!brandId) return null;
  try {
    const stored = window.localStorage.getItem(storageKey(brandId));
    return stored && !Number.isNaN(Date.parse(stored)) ? stored : null;
  } catch {
    return null;
  }
}

function writeLastSeen(brandId: string): void {
  try {
    window.localStorage.setItem(storageKey(brandId), new Date().toISOString());
  } catch {
    /* Storage is blocked or full: the next visit simply marks nothing new. */
  }
}

export function useTrendsLastSeen(brandId: string | null): string | null {
  const [seen, setSeen] = useState(() => ({ brandId, at: readLastSeen(brandId) }));

  /* A brand switch is a new visit to a different wall: read that brand's time.
     Adjusted while rendering, so the first paint for the new brand already
     marks against its own last visit rather than the previous brand's. */
  if (seen.brandId !== brandId) {
    setSeen({ brandId, at: readLastSeen(brandId) });
  }

  useEffect(() => {
    if (!brandId) return;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") writeLastSeen(brandId);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      writeLastSeen(brandId);
    };
  }, [brandId]);

  return seen.brandId === brandId ? seen.at : null;
}
